import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import {
  AppointmentType,
  DayOfWeek,
  DAYS_OF_WEEK,
  Schedule
} from "@/types/schedule";
import { Plus, Save } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

interface ScheduleFormProps {
  doctorId: string;
  scheduleToEdit?: Schedule | null;
  onScheduleAdd: (schedule: Omit<Schedule, "id">) => void;
  onScheduleUpdate?: (schedule: Schedule) => void;
  onComplete?: () => void;
}

export const ScheduleForm = ({ 
  doctorId, 
  scheduleToEdit, 
  onScheduleAdd, 
  onScheduleUpdate,
  onComplete 
}: ScheduleFormProps) => {
  const [appointmentType, setAppointmentType] = useState<string>("");
  const [selectedDays, setSelectedDays] = useState<DayOfWeek[]>([]);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [intervaloMinutos, setIntervaloMinutos] = useState<number>(30);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [tiposAgenda, setTiposAgenda] = useState<Array<{ id: number; nome?: string; descricao?: string }>>([]);

  // Função para formatar data para input
  const formatDateForInput = (date: Date): string => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Buscar tipos de atendimento da API (apenas os habilitados)
  useEffect(() => {
    const carregarTiposAtendimento = async () => {
      try {
        const response = await api.get('/api/tipo_atendimento');
        console.log('Resposta da API tipo_atendimento:', response);
        
        if (response?.result) {
          // Filtrar apenas os tipos habilitados para agenda (apenas os que têm habilitado_agenda === true)
          const tiposHabilitados = response.result.filter(
            (tipo: any) => tipo.habilitado_agenda === true
          );
          setTiposAgenda(tiposHabilitados);
          
          // Se não há tipo selecionado e há tipos disponíveis, seleciona o primeiro
          if (!appointmentType && tiposHabilitados.length > 0) {
            setAppointmentType(tiposHabilitados[0].id.toString());
          }
        } else {
          console.warn('Resposta da API não contém result:', response);
          toast.error('Erro ao carregar tipos de atendimento', {
            description: 'A resposta da API não contém os dados esperados'
          });
        }
      } catch (error: any) {
        console.error('Erro ao carregar tipos de atendimento:', error);
        toast.error('Erro ao carregar tipos de atendimento', {
          description: error?.response?.data?.message || error.message || 'Não foi possível carregar os tipos de atendimento'
        });
      }
    };

    carregarTiposAtendimento();
  }, []);

  // Preenche o formulário quando há um schedule para editar
  useEffect(() => {
    if (scheduleToEdit) {
      // Se o schedule tem id_tipo_agenda, usa ele, senão usa o appointmentType antigo
      const tipoId = (scheduleToEdit as any).id_tipo_agenda 
        ? (scheduleToEdit as any).id_tipo_agenda.toString() 
        : scheduleToEdit.appointmentType;
      setAppointmentType(tipoId);
      setSelectedDays([scheduleToEdit.dayOfWeek]);
      setStartTime(scheduleToEdit.startTime);
      setEndTime(scheduleToEdit.endTime);
      setIntervaloMinutos(scheduleToEdit.intervaloMinutos || 30);
      setStartDate(scheduleToEdit.startDate ? formatDateForInput(scheduleToEdit.startDate) : "");
      setEndDate(scheduleToEdit.endDate ? formatDateForInput(scheduleToEdit.endDate) : "");
    } else {
      // Reset form quando não há schedule para editar
      if (tiposAgenda.length > 0) {
        setAppointmentType(tiposAgenda[0].id.toString());
      } else {
        setAppointmentType("");
      }
      setSelectedDays([]);
      setStartTime("");
      setEndTime("");
      setStartDate("");
      setEndDate("");
    }
  }, [scheduleToEdit, tiposAgenda]);

  const handleDayToggle = (dayId: DayOfWeek) => {
    setSelectedDays((prev) =>
      prev.includes(dayId) ? prev.filter((d) => d !== dayId) : [...prev, dayId]
    );
  };

  // Função para validar se o formulário está completo
  const isFormValid = (): boolean => {
    // Campos obrigatórios básicos
    if (!appointmentType || !startTime || !endTime || !startDate) {
      return false;
    }

    // Precisa ter pelo menos 1 dia selecionado
    if (selectedDays.length === 0) {
      return false;
    }

    // Validação de horário (início deve ser menor que término)
    if (startTime >= endTime) {
      return false;
    }

    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!isFormValid()) {
      if (startTime >= endTime) {
        toast.error("Horário de início deve ser menor que o horário de término");
      } else {
        toast.error("Por favor, preencha todos os campos obrigatórios");
      }
      return;
    }

    // Se está editando, atualiza apenas o dia selecionado
    if (scheduleToEdit && onScheduleUpdate) {
      const updatedSchedule: Schedule = {
        ...scheduleToEdit,
        dayOfWeek: selectedDays[0],
        startTime,
        endTime,
        intervaloMinutos,
        appointmentType: appointmentType as AppointmentType, // Mantém compatibilidade
        startDate: new Date(startDate),
        endDate: endDate ? new Date(endDate) : undefined,
        ...(appointmentType && { id_tipo_agenda: Number(appointmentType) } as any), // Adiciona id_tipo_agenda
      };

      onScheduleUpdate(updatedSchedule);
      toast.success("Horário atualizado com sucesso!");
    } else {
      // Se está criando, cria para cada dia selecionado
      selectedDays.forEach((day) => {
        const newSchedule: Omit<Schedule, "id"> = {
          doctorId,
          dayOfWeek: day,
          startTime,
          endTime,
          intervaloMinutos,
          appointmentType: appointmentType as AppointmentType, // Mantém compatibilidade
          startDate: new Date(startDate),
          endDate: endDate ? new Date(endDate) : undefined,
          ...(appointmentType && { id_tipo_agenda: Number(appointmentType) } as any), // Adiciona id_tipo_agenda
        };

        onScheduleAdd(newSchedule);
      });

      toast.success("Horário(s) cadastrado(s) com sucesso!");
    }

    // Reset form apenas se não estiver editando
    if (!scheduleToEdit) {
      if (tiposAgenda.length > 0) {
        setAppointmentType(tiposAgenda[0].id.toString());
      } else {
        setAppointmentType("");
      }
      setSelectedDays([]);
      setStartTime("");
      setEndTime("");
      setStartDate("");
      setEndDate("");
    }

    // Chama callback de conclusão se fornecido
    if (onComplete) {
      onComplete();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="appointmentType">Tipo de Atendimento *</Label>
        <Select
          value={appointmentType}
          onValueChange={(value) => setAppointmentType(value)}
        >
          <SelectTrigger id="appointmentType">
            <SelectValue placeholder="Selecione o tipo" />
          </SelectTrigger>
          <SelectContent>
            {tiposAgenda.length > 0 ? (
              tiposAgenda.map((tipo) => (
                <SelectItem key={tipo.id} value={tipo.id.toString()}>
                  {tipo.nome || tipo.descricao}
                </SelectItem>
              ))
            ) : (
              <div className="px-2 py-1.5 text-sm text-muted-foreground">
                Carregando tipos...
              </div>
            )}
          </SelectContent>
        </Select>
      </div>

      {!scheduleToEdit && (
        <div className="space-y-3">
          <Label htmlFor="dias-atendimento">Dias de Atendimento *</Label>
          <div 
            id="dias-atendimento"
            className="grid grid-cols-1 md:grid-cols-2 gap-3"
          >
            {DAYS_OF_WEEK && DAYS_OF_WEEK.length > 0 ? (
              DAYS_OF_WEEK.map((day) => (
                <div key={day.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={day.id}
                    checked={selectedDays.includes(day.id)}
                    onCheckedChange={() => handleDayToggle(day.id)}
                  />
                  <Label
                    htmlFor={day.id}
                    className="text-sm font-normal cursor-pointer"
                  >
                    {day.label}
                  </Label>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">Carregando dias...</p>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="startTime">Horário de Início *</Label>
          <Input
            id="startTime"
            type="time"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="endTime">Horário de Término *</Label>
          <Input
            id="endTime"
            type="time"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="intervaloMinutos">Intervalo em Minutos *</Label>
        <Input
          id="intervaloMinutos"
          type="number"
          min="15"
          max="120"
          step="15"
          value={intervaloMinutos}
          onChange={(e) => setIntervaloMinutos(Number(e.target.value))}
        />
        <p className="text-xs text-muted-foreground">
          Intervalo entre os horários (ex: 30 minutos = 08:00, 08:30, 09:00...)
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="startDate">Data de Início *</Label>
          <Input
            id="startDate"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="endDate">Data de Término (opcional)</Label>
          <Input
            id="endDate"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            Deixe vazio para validade indeterminada
          </p>
        </div>
      </div>

      <Button type="submit" className="w-full md:w-auto" disabled={!isFormValid()}>
        {scheduleToEdit ? (
          <>
            <Save className="h-4 w-4 mr-2" />
            Salvar Alterações
          </>
        ) : (
          <>
            <Plus className="h-4 w-4 mr-2" />
            Adicionar Horário
          </>
        )}
      </Button>
    </form>
  );
};

